<template>
  <q-dialog
    id="session-card-dialog"
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <div
      ref="dialogContent"
      class="session-card-dialog"
      :class="{ 'session-card-dialog--mobile': $q.platform.is.mobile }"
    >
      <session-details-card
        ref="sessionDetailsCard"
        :all-programs="allPrograms"
        :client="client"
        :session="session"
        :open-session-activity="openSessionActivity"
        :current-time="currentVideoTime"
        :read-only="readOnly"
        :editable="true"
        :nav-controller="navController"
        close-button
        class="q-pa-md"
        @play-video="onPlayVideo"
        @update-video-info="onUpdateVideoInfo"
        @video-seek="onVideoSeek"
        @close="onDialogClose"
        @delete-session="onDeleteSession"
        @scroll-to-banner="scrollToChangeRequestBanner"
      />
    </div>

    <session-video
      v-if="hasActiveVideo"
      ref="sessionVideo"
      :video-player-info="videoPlayerInfo"
      @update-time="onUpdateVideoTime"
      @close="onCloseVideoPlayer"
    />
  </q-dialog>
</template>
<script>
import SessionDetailsCard from 'components/Sessions/SessionDetailsCard';
import SessionVideo from 'components/Sessions/SessionVideo';
import { cloneDeep, get, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import { scroll } from 'quasar';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';

export default {
  name: 'SessionCardDialog',

  components: {
    SessionDetailsCard,
    SessionVideo,
  },

  mixins: [Interactions],

  props: {
    allPrograms: {
      type: Array,
      required: true,
    },
    client: {
      type: Object,
      required: true,
    },
    session: {
      type: Object,
      required: true,
    },
    openSessionActivity: {
      type: String,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    // Controller from useActivityNavigation; when set, the card shows back/next.
    navController: {
      type: Object,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      videoPlayerInfo: null,
      currentVideoTime: null,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'hasSupervisorCredentials']),

    hasActiveVideo() {
      return !isEmpty(this.videoPlayerInfo);
    }, // hasActiveVideo()

    sessionNameFormatted() {
      return this.session.sessionName || this.$t('sessions.sessionCard.adHocSessionTitle');
    },
  },

  mounted() {
    this.navController?.registerCloser?.(this.closeForNavigation);
  },

  beforeUnmount() {
    this.navController?.unregisterCloser?.(this.closeForNavigation);
  },

  methods: {
    show() {
      if (!this.isSessionVisible()) {
        this.notifyNegative(this.$t('sessions.sessionCard.sessionNotVisible'));
        return;
      }
      this.$refs.dialog.show();
    },

    // Closed by navigation, when the next activity needs a different dialog. Goes
    // through the card's regular close path so program/trial data is released and
    // the table row refreshed.
    async closeForNavigation() {
      const card = this.$refs.sessionDetailsCard;
      if (card) {
        await card.onSuccessCloseDialog();
        return;
      }
      this.hide();
    }, // closeForNavigation()

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    }, // onDialogHide()

    onDialogClose() {
      this.$emit('ok');
      this.hide();
    },

    onDeleteSession(sessionId) {
      this.$emit('ok');
      this.hide();
    }, // onDeleteSession()

    onPlayVideo(videoPlayerInfo) {
      this.videoPlayerInfo = cloneDeep(videoPlayerInfo);
    }, // onPlayVideo()

    onUpdateVideoTime(videoTime) {
      this.currentVideoTime = videoTime;
    }, // onUpdateVideoTime()

    onUpdateVideoInfo(videoPlayerInfo) {
      // if (!this.hasActiveVideo) {
      //   return;
      // }
      this.videoPlayerInfo = cloneDeep(videoPlayerInfo);
    }, // onUpdateVideoInfo()

    onVideoSeek(timestamp) {
      if (this.$refs.sessionVideo) {
        this.$refs.sessionVideo.setCurrentTime(timestamp);
      }
    }, // onVideoSeek()

    onCloseVideoPlayer() {
      this.videoPlayerInfo = null;
    }, // onCloseVideoPlayer()

    isSessionVisible() {
      return this.hasSupervisorCredentials
        || !(this.session.limitedVisibility
          && this.session.limitHistoryVisibility
          && !(get(this.session, 'visibility', []).includes(this.userDetails.userPrivilege || this.userDetails.role)));
    }, // isSessionVisible()

    scrollToChangeRequestBanner(bannerElement) {
      if (bannerElement) {
        const scrollableParent = this.$refs.dialogContent;

        if (scrollableParent) {
          const bannerOffset = bannerElement.offsetTop;
          scroll.setVerticalScrollPosition(scrollableParent, bannerOffset, 300);
        }
      }
    }, // scrollToChangeRequestBanner
  },
};
</script>

<style scoped lang="scss">
.session-card-dialog {
  min-width: 80vw;
  max-width: 80vw;

  &--mobile {
    max-width: unset;
  }
}
</style>
